<template>
  <div>
    <div class="calculator-container">
      <h1 class="h3">
        Hypothesis Test Calculator
      </h1>
      <div class="hypothesis-input">
        <div class="first">
          <div class="label-tooltip">
            <label>
              Null Hypothesis (<span class="null-alternate-symbols">H<sub>o</sub></span>)
            </label>
            <div class="icon-info-tooltip">
              <IconInfo />
              <Tooltip>
                <span>Choose the hypothesis test’s direction.</span>
              </Tooltip>
            </div>
          </div>
          <div class="null">
            <p>μ</p>
            <Dropdown
              calculator
              class="null-hypothesis-dropdown"
              selected-title="="
              :selected="selectedOperator"
              :radio-buttons="{ '≥': '≥', '≤': '≤', '=': '=' }"
              @change="currentSelectedOperator"
            />
            <input v-model="nullHypothesis" type="text">
          </div>
          <div v-if="nullHypothesisAlert" class="error">
            {{ nullHypothesisAlert }}
          </div>
        </div>
        <div class="second">
          <div class="label-tooltip">
            <label>
              Alternative Hypothesis (<span class="null-alternate-symbols">H<sub>a</sub></span>)
            </label>
          </div>
          <div class="alternate">
            <p>μ</p>
            <input :value="`${alternateHypothesisOperator} ${nullHypothesis}`" type="text" readonly>
          </div>
        </div>
      </div>
      <span class="your-data">
        Your Data
      </span>
      <Dropdown
        class="calculation-method"
        calculator
        :selected-title="firstMethodName"
        :selected="selectedCalculationMethod"
        :radio-buttons="{ readyData: firstMethodName, rawData: 'Raw Data' }"
        @change="currentSelectedCalculationMethod"
      />
      <form @submit.prevent="calculations">
        <div v-if="selectedCalculationMethod === 'readyData'" class="sample-inputs">
          <div>
            <div class="label-tooltip">
              <label class="sample-mean-label">
                Sample Mean <span>(x̄)</span>
              </label>
              <div class="icon-info-tooltip">
                <IconInfo />
                <Tooltip>
                  <span>Provide a positive numerical value.</span>
                </Tooltip>
              </div>
            </div>
            <input
              v-model="mean" type="text"
              :class="{ 'invalid-value': meanAlert }"
            >
            <div v-if="meanAlert" class="error">
              {{ meanAlert }}
            </div>
          </div>
          <div class="sample-size">
            <div class="label-tooltip">
              <label>
                Sample Size (n)
              </label>
              <div class="icon-info-tooltip">
                <IconInfo />
                <Tooltip>
                  <span>Provide a positive numerical integer.</span>
                </Tooltip>
              </div>
            </div>
            <input
              v-model="size" type="text"
              :class="{ 'invalid-value': sizeAlert }"
            >
            <div v-if="sizeAlert" class="error">
              {{ sizeAlert }}
            </div>
          </div>
        </div>
        <div v-else-if="selectedCalculationMethod === 'rawData'" class="raw-data">
          <div class="label-tooltip">
            <label class="type-data">
              Type Your Data
            </label>
            <div class="icon-info-tooltip">
              <IconInfo />
              <Tooltip>
                <span>Input the data separated by comma, space or enter.</span>
              </Tooltip>
            </div>
          </div>
          <div class="textarea-border" :class="{ 'invalid-value': textareaAlert }">
            <textarea
              v-model="rawDataMean"
              cols="30" rows="10"
              placeholder="7,1,6,2,11

7 1 6 2 11

7
1
6"
            />
          </div>
          <div v-if="textareaAlert" class="error">
            {{ textareaAlert }}
          </div>
        </div>
        <div class="standard-deviation">
          <div class="question-dropdown" :class="{ 'raw-data-version': hidePopulationSampleInput }">
            <label>
              Do you know the population standard deviation (σ)?
            </label>
            <Dropdown
              calculator
              class="small-dropdown"
              selected-title="Yes"
              :selected="selectedAnswer"
              :radio-buttons="{ Yes: 'Yes', No: 'No' }"
              @change="currentSelectedAnswer"
            />
          </div>
          <div v-if="!hidePopulationSampleInput" class="population-sample">
            <div class="label-tooltip">
              <label>
                {{ populationOrSample[0] }}
              </label>
              <div class="icon-info-tooltip">
                <IconInfo />
                <Tooltip :class="{ 'confidence-population-tooltip': selectedCalculationMethod === 'readyData' }">
                  <span v-if="selectedCalculationMethod === 'readyData'">Provide a numerical and positive standard deviation value.</span>
                  <span v-else>Provide a numerical positive value.</span>
                </Tooltip>
              </div>
            </div>
            <input
              v-model="populationSampleData[populationOrSample[1]]" type="text"
              :class="{ 'invalid-value': populationSampleData[populationOrSample[2]] }"
            >
            <div
              v-if="populationSampleData[populationOrSample[2]] && !hidePopulationSampleInput"
              class="error"
            >
              {{ populationSampleData[populationOrSample[2]] }}
            </div>
          </div>
        </div>
        <div
          class="significance-level-decimal-places"
        >
          <div class="inputs-tooltip">
            <div class="label-tooltip">
              <label>
                Significance Level
              </label>
              <div class="icon-info-tooltip significance">
                <IconInfo />
                <Tooltip>
                  <span>Provide a positive numerical significance level.</span>
                </Tooltip>
              </div>
            </div>
            <div class="input-dropdown">
              <input
                v-model="selectedSignificanceLevel" type="text"
                :class="{ 'invalid-value': significanceLevelAlert, 'dropdown-opened': dropdownOpened }"
              >
              <Dropdown
                calculator
                calculator-input-dropdown-combination
                class="small-dropdown"
                :class="{ 'add-background': arrowHover, 'invalid-value': significanceLevelAlert }"
                :selected="selectedSignificanceLevel"
                :radio-buttons="{ '0.01': '0.01', '0.05': '0.05', '0.10': '0.10' }"
                @change="currentSelectedSignificanceLevel"
                @arrowHover="arrowHover = true"
                @arrowLeave="arrowHover = false"
                @arrowClicked="dropdownOpened = !dropdownOpened"
                @dropdownClosed="dropdownOpened = false"
              />
            </div>
            <div v-if="significanceLevelAlert" class="error">
              {{ significanceLevelAlert }}
            </div>
          </div>
          <div class="decimal-places">
            <div class="label-tooltip">
              <label>
                Decimal Places
              </label>
              <div class="icon-info-tooltip">
                <IconInfo />
                <Tooltip>
                  <span>Choose the number of decimal places.</span>
                </Tooltip>
              </div>
            </div>
            <Dropdown
              calculator
              calculator-decimal-places
              class="small-dropdown"
              :selected="selectedDecimalPlaces"
              :radio-buttons="{ 1: '1', 2: '2', 3: '3', 4: '4', 5: '5', 6: '6', 7: '7', 8: '8', 9: '9' }"
              @change="currentSelectedDecimalPlace"
            />
          </div>
        </div>
        <div class="buttons">
          <button class="btn-primary calculate">
            Calculate
          </button><button
            class="btn-primary-light reset"
            @click="reset"
          >
            Reset
          </button>
          <button class="load-example btn-link-accent" @click="loadExample">
            Load Example
          </button>
        </div>
      </form>
    </div>

    <div v-show="$store.state.calculators.result" id="interpretation" class="interpretation-container">
      <h4>Interpretation:</h4>
      <p><span class="colored-texts">The p-value equals {{ pValue }}.</span> Since the p-value {{ comparisonCannotArr[0] }} {{ selectedSignificanceLevel }}, we <span class="bolded-texts">{{ comparisonCannotArr[1] }} reject</span> <span class="inline-formulas">\[H_0\]</span></p>
      <div v-if="comparisonCannotArr[0] === '>'">
        <p>In other words, your sample data does not provide sufficient evidence that the population mean is significantly lower than the hypothesized value at the {{ selectedSignificanceLevel }} significance level.</p>
        <p>Note that failing to reject the null hypothesis doesn’t prove it's true. It just suggests that there isn’t enough evidence against it based on your data and chosen significance level.</p>
      </div>
      <div v-else>
        <p>In other words, your sample data provides sufficient evidence that the population mean is significantly {{ lowerGreaterDifferent }} than the hypothesized value at the {{ selectedSignificanceLevel }} significance level.</p>
        <p>Note that rejecting the null hypothesis doesn’t prove the alternative; it just supports it.</p>
      </div>
    </div>
  </div>
</template>

<script>
import calculatorsMainMixin from '@itbpbg/lms-components/src/mixins/calculators/main';
import Dropdown from '@itbpbg/lms-components/src/components/Dropdown.vue';
import Tooltip from '@itbpbg/lms-components/src/components/Tooltip.vue';
import IconInfo from '@itbpbg/lms-components/src/assets/svg/icon-info.svg?vue-component';
import jStat from 'jstat';

export default {
  components: {
    Dropdown,
    Tooltip,
    IconInfo,
  },
  mixins: [calculatorsMainMixin],
  data() {
    return {
      pValue: '',
      selected: '',
      selectedOperator: '=',
      alternateHypothesisOperator: '',
      selectedCalculationMethod: 'readyData',
      selectedSignificanceLevel: '0.05',
      selectedDecimalPlaces: '4',
      selectedAnswer: 'No',
      mean: '',
      rawDataMeanArray: [],
      rawDataMean: '',
      size: '',
      populationSampleData: {
        populationS: '',
        sampleS: '',
        populationAlert: false,
        sampleAlert: false,
      },
      meanAlert: false,
      sizeAlert: false,
      significanceLevelAlert: false,
      nullHypothesisAlert: false,
      textareaAlert: false,
      arrowHover: false,
      resultNumber: 0,
      dropdownOpened: false,
      nullHypothesis: '',
      zScoreTable: {
        99: 2.575829,
        98: 2.326348,
        97: 2.17009,
        96: 2.053749,
        95: 1.959964,
        94: 1.880794,
        93: 1.811911,
        92: 1.750686,
        91: 1.695398,
        90: 1.644854,
        89: 1.598193,
        88: 1.554774,
        87: 1.514102,
        86: 1.475791,
        85: 1.439531,
        84: 1.405072,
        83: 1.372204,
        82: 1.340755,
        81: 1.310579,
        80: 1.281552,
        79: 1.253565,
        78: 1.226528,
        77: 1.200359,
        76: 1.174987,
        75: 1.150349,
        74: 1.126391,
        73: 1.103063,
        72: 1.080319,
        71: 1.058122,
        70: 1.036433,
        69: 1.015222,
        68: 0.994458,
        67: 0.974114,
        66: 0.954165,
        65: 0.934589,
        64: 0.915365,
        63: 0.896473,
        62: 0.877896,
        61: 0.859617,
        60: 0.841621,
        59: 0.823894,
        58: 0.806421,
        57: 0.789192,
        56: 0.772193,
        55: 0.755415,
        54: 0.738847,
        53: 0.722479,
        52: 0.706303,
        51: 0.690309,
        50: 0.67449,
        49: 0.658838,
        48: 0.643345,
        47: 0.628006,
        46: 0.612813,
        45: 0.59776,
        44: 0.582842,
        43: 0.568051,
        42: 0.553385,
        41: 0.538836,
        40: 0.524401,
        39: 0.510073,
        38: 0.49585,
        37: 0.481727,
        36: 0.467699,
        35: 0.453762,
        34: 0.439913,
        33: 0.426148,
        32: 0.412463,
        31: 0.398855,
        30: 0.38532,
        29: 0.371856,
        28: 0.358459,
        27: 0.345126,
        26: 0.331853,
        25: 0.318639,
        24: 0.305481,
        23: 0.292375,
        22: 0.279319,
        21: 0.266311,
        20: 0.253347,
        19: 0.240426,
        18: 0.227545,
        17: 0.214702,
        16: 0.201893,
        15: 0.189118,
        14: 0.176374,
        13: 0.163658,
        12: 0.150969,
        11: 0.138304,
        10: 0.125661,
        9: 0.113039,
        8: 0.100434,
        7: 0.0878448,
        6: 0.0752699,
        5: 0.0627068,
        4: 0.0501536,
        3: 0.0376083,
        2: 0.0250689,
        1: 0.0125335,
      },
    };
  },
  computed: {
    populationOrSample() {
      return this.selectedAnswer === 'Yes' || this.selectedAnswer === ''
        ? ['Population Standard Deviation (σ)', 'populationS', 'populationAlert']
        : ['Sample Standard Deviation (s)', 'sampleS', 'sampleAlert'];
    },
    hidePopulationSampleInput() {
      return this.selectedCalculationMethod === 'rawData' && this.selectedAnswer === 'No';
    },
    firstMethodName() {
      return this.$store.state.layout !== 'desktop' ? 'Mean, SD and n' : 'Mean, Standard Deviation and Sample Size';
    },
    hideResultVariablesArray() {
      return [
        this.mean,
        this.size,
        this.populationSampleData.sampleS,
        this.populationSampleData.populationS,
        this.selectedSignificanceLevel,
        this.selectedDecimalPlaces,
        this.rawDataMean,
        this.selectedAnswer,
        this.nullHypothesis,
        this.selectedOperator,
      ];
    },
    nullAlternateHypothesisArr() {
      return [
        this.selectedOperator,
        this.nullHypothesis,
      ];
    },
    comparisonCannotArr() {
      if (Number(this.pValue) > Number(this.selectedSignificanceLevel)) {
        return ['>', 'cannot'];
      }

      return Number(this.pValue) === Number(this.selectedSignificanceLevel) ? ['=', ''] : ['<', ''];
    },
    lowerGreaterDifferent() {
      if (this.selectedOperator === '≥') {
        return 'lower';
      }

      if (this.selectedOperator === '=') {
        return 'different';
      }

      return 'greater';
    },
  },
  watch: {
    selectedCalculationMethod() {
      this.defaultingInput();
    },
    hideResultVariablesArray() {
      this.hideResults();
    },
    nullAlternateHypothesisArr() {
      if (this.nullHypothesis !== '') {
        if (this.selectedOperator === '=') {
          this.alternateHypothesisOperator = '≠';
        } else if (this.selectedOperator === '≥') {
          this.alternateHypothesisOperator = '<';
        } else {
          this.alternateHypothesisOperator = '>';
        }
      } else {
        this.alternateHypothesisOperator = '';
      }
    },
  },
  methods: {
    scrollToSection() {
      this.$nextTick(() => {
        let yOffset;
        if (this.$store.state.layout === 'desktop') {
          yOffset = -90;
        } else {
          yOffset = this.$config.project === 'lms' ? -64 : -68;
        }

        const section = 'interpretation';
        const element = document.querySelector(`#${section}`);
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;

        setTimeout(() => {
          this.clickedSection = section;
          window.scrollTo({ top: y, behavior: 'smooth' });

          setTimeout(() => {
            this.clickedSection = null;
          }, 500);
        }, 200);
      });
    },
    reset(e) {
      this.selectedCalculationMethod = 'readyData';
      this.defaultingInput();
      e.preventDefault();
    },
    defaultingInput() {
      this.selectedOperator = '=';
      this.nullHypothesis = '';
      this.mean = '';
      this.rawDataMean = '';
      this.size = '';
      this.populationSampleData[this.populationOrSample[1]] = '';
      this.removeAlerts();
      this.selectedSignificanceLevel = '0.05';
      this.selectedDecimalPlaces = '4';
      this.selectedAnswer = 'No';
      this.$store.commit('calculators/result', false);
    },
    hideResults() {
      this.$store.commit('calculators/result', false);
      this.removeAlerts();
    },
    loadExample(e) {
      this.hideResults();
      this.removeAlerts();
      this.selectedOperator = '=';
      this.selectedSignificanceLevel = '0.05';
      this.selectedDecimalPlaces = '4';
      this.nullHypothesis = '115';

      if (this.selectedCalculationMethod === 'readyData') {
        this.mean = '100.952';
        this.size = '30';
        if (this.selectedAnswer === 'No') {
          this.populationSampleData[this.populationOrSample[1]] = '11477.45';
        } else {
          this.populationSampleData[this.populationOrSample[1]] = '15';
        }
      } else {
        this.rawDataMean = '117.511\n104.106\n113.820\n103.389\n84.759\n114.131\n81.103\n101.304\n105.220\n87.976\n92.498\n96.292\n92.001\n104.197\n87.170\n112.783\n110.003\n97.399\n93.150\n114.672\n111.176\n109.456\n116.126\n89.859\n82.854\n89.498\n114.678\n91.424\n95.270\n114.743';
        if (this.selectedAnswer === 'Yes') {
          this.populationSampleData[this.populationOrSample[1]] = '15';
        }
      }

      e.preventDefault();
    },
    removeAlerts() {
      const alertsArray = ['nullHypothesisAlert', 'meanAlert', 'sizeAlert', this.populationOrSample[2], 'significanceLevelAlert', 'textareaAlert'];

      for (let i = 0; i < alertsArray.length; i += 1) {
        if (alertsArray[i] === 'populationAlert' || alertsArray[i] === 'sampleAlert') {
          this.populationSampleData[alertsArray[i]] = false;
        } else {
          this[alertsArray[i]] = false;
        }
      }
    },
    currentSelectedOperator(selectedOperator) {
      this.selectedOperator = selectedOperator;
    },
    currentSelectedSignificanceLevel(selectedSignificanceLevel) {
      this.selectedSignificanceLevel = selectedSignificanceLevel;
    },
    inputValidation() {
      this.removeAlerts();

      let validationResult = true;
      this.selectedSignificanceLevel = this.selectedSignificanceLevel.trim();
      this.populationSampleData[this.populationOrSample[1]] = this.populationSampleData[this.populationOrSample[1]].trim();

      if (Number.isNaN(Number(this.nullHypothesis))) {
        this.nullHypothesisAlert = 'Invalid Value';
        validationResult = false;
      } else if (this.nullHypothesis === '') {
        this.nullHypothesisAlert = 'Field required';
        validationResult = false;
      }

      if (this.selectedSignificanceLevel.length > 4) {
        this.significanceLevelAlert = 'Number of decimal places is limited to 2.';
        validationResult = false;
      } else if (this.selectedSignificanceLevel < 0.01 || this.selectedSignificanceLevel > 1) {
        this.significanceLevelAlert = 'The number must be between 0 and 1.';
        validationResult = false;
      }

      if (this.selectedCalculationMethod === 'readyData') {
        this.mean = this.mean.trim();
        this.size = this.size.trim();

        const alertsArray = ['meanAlert', 'sizeAlert', this.populationOrSample[2], 'significanceLevelAlert'];
        const inputValuesArray = [this.mean, this.size, this.populationSampleData[this.populationOrSample[1]], this.selectedSignificanceLevel];
        const invalidValuesArray = inputValuesArray.map((value) => ((value === '' || (Number.isNaN(Number(value)) || value < 0)) ? value : 'valid'));

        for (let i = 0; i < invalidValuesArray.length; i += 1) {
          if (invalidValuesArray[i] !== 'valid') {
            if (invalidValuesArray[i] === '') {
              if (alertsArray[i] === 'populationAlert' || alertsArray[i] === 'sampleAlert') {
                this.populationSampleData[alertsArray[i]] = 'Field required';
              } else {
                this[alertsArray[i]] = 'Field required';
              }
            } else if (alertsArray[i] === 'populationAlert' || alertsArray[i] === 'sampleAlert') {
              this.populationSampleData[alertsArray[i]] = 'Invalid value';
            } else {
              this[alertsArray[i]] = 'Invalid value';
            }

            validationResult = false;
          }
        }

        return validationResult;
      }

      this.rawDataMeanArray = this.rawDataMean.split(/,|\s+|\n+/).filter((value) => value !== '');

      if (this.rawDataMeanArray.length === 0) {
        this.textareaAlert = 'Field required';
        this.rawDataMean = '';
        validationResult = false;
      } else {
        for (let i = 0; i < this.rawDataMeanArray.length; i += 1) {
          if (this.rawDataMeanArray[i] < 0 || Number.isNaN(Number(this.rawDataMeanArray[i]))) {
            this.textareaAlert = 'Invalid value';
            validationResult = false;
          }
        }
      }

      if (this.populationSampleData[this.populationOrSample[1]] === '') {
        this.populationSampleData[this.populationOrSample[2]] = 'Field required';
      } else if (this.populationSampleData[this.populationOrSample[1]] < 0
       || Number.isNaN(Number(this.populationSampleData[this.populationOrSample[1]]))) {
        this.populationSampleData[this.populationOrSample[2]] = 'Invalid Value';
      }

      if (this.populationSampleData.populationAlert !== false) {
        validationResult = false;
      }

      return validationResult;
    },
    calculations() {
      if (!this.inputValidation()) {
        return;
      }

      let mean = 0;
      let size = 0;
      let standardDeviation = Number(this.populationSampleData[this.populationOrSample[1]]);
      let currentScore = 0;
      let testStatistic = 0;

      if (this.selectedCalculationMethod === 'readyData') {
        mean = Number(this.mean);
        size = Number(this.size);
      } else {
        for (let i = 0; i < this.rawDataMeanArray.length; i += 1) {
          mean += Number(this.rawDataMeanArray[i]);
        }

        mean /= this.rawDataMeanArray.length;
        size = this.rawDataMeanArray.length;
      }

      const getTScore = () => {
        const probability = this.selectedOperator === '=' ? this.selectedSignificanceLevel / 2 : this.selectedSignificanceLevel;
        currentScore = Math.abs(jStat.studentt.inv(probability, size - 1));
      };

      if (this.selectedAnswer === 'Yes') {
        const significanceLevel = this.selectedOperator === '=' ? this.selectedSignificanceLevel : this.selectedSignificanceLevel * 2;
        if (size >= 30) {
          currentScore = this.zScoreTable[(1 - Number(significanceLevel)) * 100];
        } else {
          getTScore();
        }

        // Calculating the test statistic
        testStatistic = (mean - this.nullHypothesis) / (standardDeviation / Math.sqrt(size));

        // Calculating the p-value
        this.pValue = jStat.normal.cdf(Math.abs(testStatistic), 0, 1);

        if (this.selectedOperator === '=') {
          this.pValue = 2 * (1 - this.pValue);
        } else if (this.selectedOperator === '≥') {
          this.pValue = 1 - this.pValue;
        }
      } else {
        getTScore();

        // Calculating standard deviation
        if (this.selectedCalculationMethod === 'rawData') {
          standardDeviation = 0;

          for (let i = 0; i < this.rawDataMeanArray.length; i += 1) {
            standardDeviation += (Number(this.rawDataMeanArray[i]) - mean) ** 2;
          }

          standardDeviation = Math.sqrt(standardDeviation / (size - 1));
        }

        // Calculating the test statistic
        testStatistic = (mean - this.nullHypothesis) / (standardDeviation / Math.sqrt(size));

        // Calculating the p-value
        this.pValue = jStat.ttest(Math.abs(testStatistic), size);

        if (this.selectedOperator === '≤') {
          this.pValue /= 2;
        } else if (this.selectedOperator === '≥') {
          this.pValue = 1 - this.pValue / 2;
        }
      }

      this.pValue = this.pValue.toFixed(this.selectedDecimalPlaces);

      this.$store.commit('calculators/result', this.resultNumber += 1);
      this.checkAndFormatResult(mean, size, standardDeviation, currentScore, testStatistic);
      this.scrollToSection();
    },
    checkAndFormatResult(mean, size, standardDeviation, currentScore, testStatistic) {
      const results = [
        Number(mean),
        Number(size),
        Number(standardDeviation),
        Number(currentScore),
        Number(testStatistic),
        Number(this.nullHypothesis),
      ];
      const onlyZerosTest = /^\d+.[0]+$/;

      for (let i = 0; i < results.length; i += 1) {
        if (results[i].toString().split('').includes('e')) {
          let numberPart = 0;
          let additionalPart = '';

          [numberPart, additionalPart] = results[i].toString().split(/(?=e)/);
          numberPart = Number(numberPart);

          if (onlyZerosTest.test(numberPart)) {
            numberPart = Math.trunc(numberPart);
          } else {
            numberPart = numberPart.toFixed(this.selectedDecimalPlaces);
          }

          results[i] = numberPart.toString().concat(additionalPart);
        }

        if (onlyZerosTest.test(results[i])) {
          results[i] = Math.trunc(results[i]);
        } else if (results[i] % 1 !== 0) {
          results[i] = results[i].toFixed(this.selectedDecimalPlaces);
        }
      }

      this.calculationData(results[0], results[1], results[2], results[3], results[4], results[5]);
    },
    calculationData(mean, size, standardDeviation, currentScore, testStatistic, nullHypothesis) {
      const onlyZerosTest = /^\d+.[0]+$/;
      const decimalTest = /^\d+\.{1}?\d+$/;
      let rawDataMeanArray = this.rawDataMeanArray.map((num) => (onlyZerosTest.test(num) ? Math.trunc(num) : num));
      rawDataMeanArray = this.rawDataMeanArray.map((num) => (decimalTest.test(num) ? Number(num).toFixed(2) : num));

      this.$store.commit('calculators/calculationData',
        {
          method: this.selectedCalculationMethod,
          answer: this.selectedAnswer,
          mean,
          size,
          nullHypothesis,
          standardDeviation,
          rawDataMeanArray,
          rawDataMeanArrayActualInput: this.rawDataMeanArray.map(Number),
          significanceLevel: this.selectedSignificanceLevel,
          currentScore,
          testStatistic,
          selectedOperator: this.selectedOperator,
          pValue: this.pValue,
          alternateHypothesisOperator: this.alternateHypothesisOperator,
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.calculator-container {
  .standard-deviation {
    min-height: 90px;
    display: flex;
    padding: 0;
    gap: 30px;
    margin-top: var(--spacing-30);

    .question-dropdown {
      .dropdown::v-deep.open .list {
        top: 102.9%;
      }

      label {
        display: inline-block;
        margin-bottom: var(--spacing-6);
        line-height: 120%;
      }

      &.raw-data-version {
        width: calc(50% - 15px);
      }
    }

    .population-sample {
      .label-tooltip {
        display: flex;
        height: 33.59px;
        align-items: flex-end;

        .icon-info-tooltip {
          height: 17px;
        }
      }

      input {
        height: 52px;
        border: 1px solid rgba(0, 12, 31, 0.18);
        border-radius: 5px;
      }

      input:focus {
        border-color: var(--main-dark);
      }

      input.invalid-value {
        border-color: #E04A4A;
      }
    }
  }

  .significance-level-decimal-places {
    display: flex;
    gap: 30px;
    margin: var(--spacing-36) 0;

    .decimal-places {
      width: 100%;
      margin: 0;

      .dropdown::v-deep.open .list {
        top: 101.9%;
      }
    }

    .input-dropdown {
      display: flex;
      position: relative;

      .dropdown.small-dropdown {
        width: 32px;
        border-left: unset;
        position: unset;
        border-radius: 0 5px 5px 0;

        &.add-background {
          background: var(--main-light-shade-2);
          border: 1px solid rgba(0, 12, 31, 0.18);
        }

        &.open {
          border: 1px solid var(--main-dark);
          border-bottom: 0;
          background: var(--main-light-shade-2);
          border-radius: 0 5px 0 0;
        }

        &.invalid-value {
          border-color: #E04A4A;
        }
      }

      .dropdown::v-deep {
        height: 52px;
        border-radius: 0 5px 5px 0;
        border: 1px solid rgba(0, 12, 31, 0.18);

        label.active span {
          font-weight: 600;
        }

        .list {
          left: unset;
        }

        .list-head {
          width: 32px;
          height: 100%;
          padding: 0;
          justify-content: center;
          max-width: unset;

          svg {
            position: unset;
          }

          span {
            display: none;
          }
        }
      }

      input {
        border-radius: 5px 0 0 5px;
        border: 1px solid rgba(0, 12, 31, 0.18);
        border-right: unset;

        &.dropdown-opened {
          border-radius: 5px 0 0 0;
          border-bottom: 0;
        }
      }

      input:focus {
        border: 1px solid var(--main-dark);
      }

      input.invalid-value {
        border-color: #E04A4A;
      }
    }
  }

  .hypothesis-input {
    display: flex;
    margin-bottom: var(--spacing-30);

    .label-tooltip {
      label {
        .null-alternate-symbols {
          font-size: 12px;
        }
      }
    }

    .error {
      padding-left: 26%;
    }

    .first {
      margin-right: var(--spacing-30);

      .null {
        display: flex;
        align-items: center;

        p {
          font-size: 18px;
          margin: 0 var(--spacing-8) 0;
        }

        .dropdown.null-hypothesis-dropdown {
          width: 65px;
          margin-right: var(--spacing-8);

          ::v-deep .list {
            li {
              div {
                margin: 0;

                label {
                  text-align: center;

                  span {
                    padding-left: 0;
                  }
                }
              }
            }
          }
        }
      }
    }

    .second {
      .label-tooltip {
        label {
          margin: 0;
        }
      }

      .alternate {
        display: flex;
        align-items: center;

        p {
          color: var(--main-dark-light-4);
          font-size: 18px;
          margin: 0 var(--spacing-8) 0;
        }

        input {
          border: 1px solid rgba(0, 12, 31, 0.1);
          color: var(--main-light-shade-4);

          &:hover {
            cursor: not-allowed;
          }

          &:focus {
            border-color: var(--main-dark-opacity-18);
          }
        }
      }
    }
  }
}

.interpretation-container.interpretation-container {
  background: var(--accent-light-1);
}

@include bp($bp-mobile) {
  .calculator-container {
    .population-sample {
      .label-tooltip .icon-info-tooltip {
        &:hover {
          .tooltip {
            left: -388%;

            &:after {
              left: 70%;
            }
          }
        }
      }
    }

    .hypothesis-input {
      margin-bottom: var(--spacing-16);
    }

    .significance-level-decimal-places {
      margin-top: var(--spacing-16);
    }
  }
}

@include bp($bp-phone) {
  .calculator-container {
    .hypothesis-input {
      flex-direction: column;

      .second {
        margin-top: var(--spacing-16);
      }
    }

    .significance-level-decimal-places {
      flex-direction: column;
      gap: var(--spacing-16);
    }
  }
}

@import '../../../../assets/sass/ds/calculators/calculators.scss';
</style>
